import type { Block } from 'blockly/core';

import { arduinoGenerator, blockTraceComment, Order, quoteString } from './arduino-generator.js';
import { ensureBeginnerSerialSetup } from './beginner-serial.js';

const BEGINNER_LED_PIN = 'LED_BUILTIN';
const BEGINNER_BUTTON_PIN = '2';
const BEGINNER_BUZZER_PIN = '8';

function statementPrefix(block: Block, code: string): string {
  return `${blockTraceComment(block)}\n${code}`;
}

function ensureLedSetup(): void {
  arduinoGenerator.addSetupOnce('beginner-led', `pinMode(${BEGINNER_LED_PIN}, OUTPUT);`);
}

function ensureButtonSetup(): void {
  arduinoGenerator.addSetupOnce(
    'beginner-button',
    `pinMode(${BEGINNER_BUTTON_PIN}, INPUT_PULLUP);`,
  );
}

function ensureBuzzerSetup(): void {
  arduinoGenerator.addSetupOnce('beginner-buzzer', `pinMode(${BEGINNER_BUZZER_PIN}, OUTPUT);`);
}

export function registerBeginnerBlockGenerators(): void {
  arduinoGenerator.forBlock['beginner_start'] = (block: Block) => {
    const branch = arduinoGenerator.statementToCode(block, 'STACK');
    if (branch.trim()) {
      arduinoGenerator.addLoop(branch.trim());
    }
    return '';
  };

  arduinoGenerator.forBlock['beginner_forever'] = (block: Block) => {
    const branch = arduinoGenerator.statementToCode(block, 'DO');
    return statementPrefix(block, `while (true) {\n${branch}}\n`);
  };

  arduinoGenerator.forBlock['beginner_wait'] = (block: Block) => {
    const seconds = Number(block.getFieldValue('SECONDS') ?? 1);
    const milliseconds = Math.max(0, Math.round(seconds * 1000));
    return statementPrefix(block, `delay(${milliseconds});\n`);
  };

  arduinoGenerator.forBlock['beginner_led_on'] = (block: Block) => {
    ensureLedSetup();
    return statementPrefix(block, `digitalWrite(${BEGINNER_LED_PIN}, HIGH);\n`);
  };

  arduinoGenerator.forBlock['beginner_led_off'] = (block: Block) => {
    ensureLedSetup();
    return statementPrefix(block, `digitalWrite(${BEGINNER_LED_PIN}, LOW);\n`);
  };

  arduinoGenerator.forBlock['beginner_blink_led'] = (block: Block) => {
    const pin = String(block.getFieldValue('PIN') ?? BEGINNER_LED_PIN);
    const pinCode = pin === 'LED_BUILTIN' ? 'LED_BUILTIN' : pin;
    arduinoGenerator.addSetupOnce(`beginner-output-${pinCode}`, `pinMode(${pinCode}, OUTPUT);`);
    const seconds = Number(block.getFieldValue('SECONDS') ?? 1);
    const milliseconds = Math.max(100, Math.round(seconds * 1000));
    return statementPrefix(
      block,
      [
        `digitalWrite(${pinCode}, HIGH);`,
        `delay(${milliseconds});`,
        `digitalWrite(${pinCode}, LOW);`,
        `delay(${milliseconds});`,
        '',
      ].join('\n'),
    );
  };

  arduinoGenerator.forBlock['beginner_button_pressed'] = (block: Block) => {
    ensureButtonSetup();
    return [`(digitalRead(${BEGINNER_BUTTON_PIN}) == LOW)`, Order.RELATIONAL];
  };

  arduinoGenerator.forBlock['beginner_buzzer'] = (block: Block) => {
    ensureBuzzerSetup();
    return statementPrefix(
      block,
      [`tone(${BEGINNER_BUZZER_PIN}, 440, 250);`, `delay(250);`, ''].join('\n'),
    );
  };

  arduinoGenerator.forBlock['beginner_display_text'] = (block: Block) => {
    ensureBeginnerSerialSetup();
    const text = quoteString(String(block.getFieldValue('TEXT') ?? ''));
    return statementPrefix(block, `Serial.println(${text});\n`);
  };
}

registerBeginnerBlockGenerators();
